import { useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Icon,
  Kanban,
  KanbanCardBody,
  KanbanTag,
  Modal,
  ModalActions,
  TextArea,
  type KanbanColor,
  type KanbanStage,
} from '@evogard/ui';
import type { SupervisedExpense } from '@evogard/contract';
import {
  EXPENSE_ACTS_WITH_REASON,
  EXPENSE_PAYMENT_METHOD_LABELS,
  EXPENSE_STAGE_TONES,
  EXPENSE_SUPERVISOR_ACT_LABELS,
  SUPERVISOR_EXPENSE_STAGES,
  SUPERVISOR_EXPENSE_STAGE_LABELS,
  format,
  formatDate,
  formatDueDate,
  formatInstant,
  fromColumn,
  type ExpenseSupervisorAct,
  type SupervisorExpenseStage,
} from '@evogard/domain';

/**
 * A cor da coluna de cada fase do supervisor — a paleta de fase do quadro,
 * escolhida pelo tom da fase (`EXPENSE_STAGE_TONES`): o neutro é cinza, o
 * pendente é amarelo, o erro é vermelho, o pago é verde.
 */
// @valor
const STAGE_COLORS: Readonly<Record<SupervisorExpenseStage, KanbanColor>> = {
  aguardando_aprovacao_lider: 'cinza',
  pendente_lider: 'amarelo',
  reprovado_lider: 'vermelho',
  aprovacao_financeiro: 'azul',
  pendente: 'laranja',
  aguardando_pagamento: 'ciano',
  pagamento_efetuado: 'verde',
  pagamento_recusado: 'vermelho',
};

const STAGES: KanbanStage[] = SUPERVISOR_EXPENSE_STAGES.map((s) => ({
  id: s,
  name: SUPERVISOR_EXPENSE_STAGE_LABELS[s],
  color: STAGE_COLORS[s],
}));

/** O valor da coluna, ou "valor ilegível" — nunca um R$ 0,00 inventado. */
export function expenseAmount(text: string): string {
  try {
    const money = fromColumn(text);
    return money === null ? '—' : format(money);
  } catch {
    return 'valor ilegível';
  }
}

/** A pílula da fase como o supervisor a vê. */
export function SupervisorStageBadge({ status }: { status: SupervisorExpenseStage }) {
  return (
    <Badge tone={EXPENSE_STAGE_TONES[status]}>
      {SUPERVISOR_EXPENSE_STAGE_LABELS[status]}
    </Badge>
  );
}

/**
 * O número da solicitação (28/10/2026, pedido do Paulo): o mesmo `#29` no
 * cartão do supervisor, no do Financeiro, na lista e no título da mesa — é
 * por ele que o líder e o Financeiro falam da mesma despesa.
 */
export function ExpenseNumber({ id, className }: { id: number; className?: string }) {
  return (
    <span
      title={`Solicitação nº ${id}`}
      className={`inline-flex shrink-0 items-center rounded-pilula border border-borda bg-superficie-suave px-2 font-semibold text-texto tabular-nums ${className ?? 'text-xs'}`}
    >
      #{id}
    </span>
  );
}

/** O beneficiário, ou o aviso de que o nome não é legível. */
export function beneficiaryOf(
  e: Pick<SupervisedExpense, 'beneficiaryName' | 'beneficiaryId'>,
): string {
  return e.beneficiaryName ?? `Beneficiário #${e.beneficiaryId}`;
}

/**
 * Quem solicitou a despesa (28/10/2026, pedido do Paulo: o líder decide
 * sabendo quem pediu, e o Financeiro também). Nulo é o sistema — o
 * lançamento automático da premiação do consultor interno.
 */
export function requesterOf(e: Pick<SupervisedExpense, 'createdByName'>): string {
  return e.createdByName ?? 'Sistema (lançamento automático)';
}

/** O miolo do cartão, na anatomia da casa: quem recebe, quanto, quem pediu, quando. */
function CardBody({ expense }: { expense: SupervisedExpense }) {
  const pending =
    (expense.status === 'pendente_lider' || expense.status === 'pendente') &&
    expense.pendingReason;
  /* A pendência do Financeiro é o que o líder tem para resolver: ela diz de quem é. */
  const fromFinance = expense.status === 'pendente';
  return (
    <KanbanCardBody
      tags={
        expense.alreadyPaid || !expense.hasTaxInvoice ? (
          <>
            {expense.alreadyPaid && <KanbanTag tone="sucesso">Já pago</KanbanTag>}
            {!expense.hasTaxInvoice && <KanbanTag>Sem nota fiscal</KanbanTag>}
          </>
        ) : undefined
      }
      title={
        <span className="flex flex-col gap-0.5">
          <span className="flex items-start justify-between gap-2">
            <span className="flex min-w-0 items-center gap-1.5">
              <ExpenseNumber id={expense.id} />
              <span className="min-w-0 truncate">{beneficiaryOf(expense)}</span>
            </span>
            <span className="shrink-0 tabular-nums">{expenseAmount(expense.amount)}</span>
          </span>
          <span className="truncate text-xs font-normal text-texto-suave">
            Solicitante: {requesterOf(expense)}
          </span>
        </span>
      }
      detail={
        <span className="tabular-nums">
          {expense.description ? `${expense.description} · ` : ''}vence{' '}
          {formatDueDate(expense.dueDate)}
        </span>
      }
      detailIcon="calendario"
      alerts={
        pending ? (
          <KanbanTag
            tone="alerta"
            title={`${fromFinance ? 'Pendência do Financeiro' : 'Pendência'}: ${expense.pendingReason}`}
          >
            {fromFinance ? 'Financeiro: ' : 'Pendência: '}
            {expense.pendingReason}
          </KanbanTag>
        ) : undefined
      }
    />
  );
}

/**
 * O quadro do supervisor da despesa (26/10/2026) — um por departamento do
 * sinistro e o da assistência. As oito colunas da supervisão, com o atraso
 * já mascarado pela API: a despesa atrasada aparece em "Aguardando
 * pagamento", e o supervisor nunca sabe que ela atrasou.
 *
 * O cartão abre a despesa, e os atos estão lá — o miolo do cartão não tem
 * botão (`Kanban`). Os atos que a pessoa tem vêm da API (`acts`), já
 * filtrados pela fase e pela permissão; nada se arrasta, porque cada ato pede
 * confirmação e alguns pedem motivo.
 */
export function SupervisorExpenseBoard({
  label,
  department,
  query,
  onAct,
}: {
  /** O nome do quadro para leitor de tela. */
  label: string;
  /**
   * O nome do departamento, no chip travado acima das colunas — ausente
   * quando a tela oferece a escolha do departamento no lugar dele.
   */
  department?: string;
  query: UseQueryResult<SupervisedExpense[]>;
  onAct: (
    id: number,
    act: ExpenseSupervisorAct,
    reason: string | null,
  ) => Promise<unknown>;
}) {
  const [openId, setOpenId] = useState<number | null>(null);
  const open = query.data?.find((e) => e.id === openId) ?? null;

  return (
    <div className="flex flex-col gap-3">
      {department !== undefined && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex min-h-9 items-center gap-1.5 rounded-pilula border border-borda bg-superficie-suave px-3 text-sm">
            Departamento: {department}
          </span>
        </div>
      )}
      {query.isError ? (
        <Alert tone="erro">{query.error.message}</Alert>
      ) : !query.data ? (
        <p className="text-sm text-texto-suave">Carregando o quadro…</p>
      ) : (
        <Kanban<SupervisedExpense>
          label={label}
          stages={STAGES}
          cardId={(e) => String(e.id)}
          cardLabel={(e) =>
            `Solicitação ${e.id}, ${beneficiaryOf(e)}, ${expenseAmount(e.amount)}`
          }
          loadPage={async (stageId) => {
            const items = (query.data ?? []).filter((e) => e.status === stageId);
            return { items, next: null, total: items.length };
          }}
          reloadKey={query.dataUpdatedAt}
          renderCard={(e) => <CardBody expense={e} />}
          onOpenCard={(e) => setOpenId(e.id)}
          emptyColumnText="Nenhuma despesa nesta fase."
        />
      )}
      {open && (
        <ExpenseActsModal expense={open} onClose={() => setOpenId(null)} onAct={onAct} />
      )}
    </div>
  );
}

/**
 * A pendência que o Financeiro devolveu ao líder (28/10/2026, pedido do
 * Paulo), no alto da despesa aberta: o que falta, quem pediu e quando — e o
 * que fazer. É a primeira coisa que o líder lê, porque é a razão de a despesa
 * ter voltado para ele.
 */
function FinancePending({ expense: e }: { expense: SupervisedExpense }) {
  if (e.status !== 'pendente') return null;
  return (
    <section
      aria-label="Pendência do Financeiro"
      className="flex gap-3 rounded-borda border border-alerta bg-alerta-suave px-3.5 py-3"
    >
      <Icon nome="pausa" size={20} className="mt-0.5 shrink-0 text-alerta" />
      <div className="flex min-w-0 flex-col gap-1.5">
        <h3 className="font-semibold text-texto">
          O Financeiro pôs esta despesa em pendência
        </h3>
        <blockquote className="rounded-borda border border-borda bg-superficie px-3 py-2 whitespace-pre-wrap text-texto">
          {e.pendingReason ?? 'O motivo não foi registrado.'}
        </blockquote>
        <p className="text-xs text-texto-suave">
          {[
            e.statusByName ? `Por ${e.statusByName}` : null,
            e.statusSince ? formatInstant(e.statusSince) : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <p className="text-xs text-texto-suave">
          Ajuste o que falta e use{' '}
          {`"${EXPENSE_SUPERVISOR_ACT_LABELS.resolver_financeiro}"`}: a despesa volta para
          a Aprovação Financeiro.
        </p>
      </div>
    </section>
  );
}

/**
 * A despesa aberta pelo supervisor: o que foi pedido, e os atos que ele tem.
 * Pendenciar e reprovar pedem motivo, e a API recusa sem ele.
 */
export function ExpenseActsModal({
  expense,
  onClose,
  onAct,
}: {
  expense: SupervisedExpense;
  onClose: () => void;
  onAct: (
    id: number,
    act: ExpenseSupervisorAct,
    reason: string | null,
  ) => Promise<unknown>;
}) {
  const [chosen, setChosen] = useState<ExpenseSupervisorAct | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const needsReason = chosen !== null && EXPENSE_ACTS_WITH_REASON.includes(chosen);

  const run = async (act: ExpenseSupervisorAct) => {
    if (EXPENSE_ACTS_WITH_REASON.includes(act) && reason.trim() === '') {
      setError('Informe o motivo.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onAct(expense.id, act, reason.trim() || null);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível concluir.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={`#${expense.id} · ${beneficiaryOf(expense)}`}
      descricao={expense.description ?? undefined}
    >
      <div className="flex flex-col gap-3 text-sm">
        <FinancePending expense={expense} />
        <div className="flex flex-wrap gap-1.5">
          <SupervisorStageBadge status={expense.status} />
          {expense.alreadyPaid && <Badge tone="sucesso">Já pago</Badge>}
          {!expense.hasTaxInvoice && <Badge tone="neutro">Sem nota fiscal</Badge>}
        </div>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
          <dt className="text-texto-suave">Solicitante</dt>
          <dd className="font-semibold">{requesterOf(expense)}</dd>
          <dt className="text-texto-suave">Valor</dt>
          <dd className="tabular-nums">{expenseAmount(expense.amount)}</dd>
          <dt className="text-texto-suave">Vencimento</dt>
          <dd className="tabular-nums">{formatDueDate(expense.dueDate)}</dd>
          <dt className="text-texto-suave">Competência</dt>
          <dd className="tabular-nums">
            {expense.competencyDate ? formatDate(expense.competencyDate) : '—'}
          </dd>
          <dt className="text-texto-suave">Forma</dt>
          <dd>{EXPENSE_PAYMENT_METHOD_LABELS[expense.paymentMethod]}</dd>
          <dt className="text-texto-suave">Lançada</dt>
          <dd>{formatInstant(expense.createdAt)}</dd>
          {expense.departmentApproverName && (
            <>
              <dt className="text-texto-suave">Aprovada por</dt>
              <dd>
                {expense.departmentApproverName}
                {expense.departmentApprovedAt
                  ? `, ${formatInstant(expense.departmentApprovedAt)}`
                  : ''}
              </dd>
            </>
          )}
          {expense.notes && (
            <>
              <dt className="text-texto-suave">Observações</dt>
              <dd className="whitespace-pre-wrap">{expense.notes}</dd>
            </>
          )}
          {expense.pendingReason && expense.status !== 'pendente' && (
            <>
              <dt className="text-texto-suave">Pendência</dt>
              <dd className="whitespace-pre-wrap">{expense.pendingReason}</dd>
            </>
          )}
          {expense.refusalReason && (
            <>
              <dt className="text-texto-suave">Motivo da reprovação</dt>
              <dd className="whitespace-pre-wrap">{expense.refusalReason}</dd>
            </>
          )}
        </dl>
        {error && <Alert tone="erro">{error}</Alert>}
        {expense.acts.length === 0 ? (
          <p className="text-texto-suave">
            {expense.status === 'aprovacao_financeiro' ||
            expense.status === 'aguardando_pagamento'
              ? 'A despesa está com o Financeiro: daqui, só ele decide.'
              : 'Não há o que decidir nesta despesa.'}
          </p>
        ) : needsReason ? (
          <>
            <TextArea
              label={chosen === 'pendenciar' ? 'O que falta' : 'Motivo da reprovação'}
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={2000}
              rows={3}
            />
            <ModalActions>
              <Button
                variant="secundario"
                onClick={() => setChosen(null)}
                disabled={busy}
              >
                Voltar
              </Button>
              <Button
                variant={chosen === 'reprovar' ? 'perigo' : 'primario'}
                loading={busy}
                onClick={() => void run(chosen)}
              >
                {EXPENSE_SUPERVISOR_ACT_LABELS[chosen]}
              </Button>
            </ModalActions>
          </>
        ) : (
          <ModalActions>
            {expense.acts.map((act) => (
              <Button
                key={act}
                variant={
                  act === 'reprovar'
                    ? 'perigo'
                    : act === 'pendenciar'
                      ? 'secundario'
                      : 'primario'
                }
                loading={busy && chosen === act}
                disabled={busy}
                onClick={() => {
                  if (EXPENSE_ACTS_WITH_REASON.includes(act)) {
                    setChosen(act);
                    setReason('');
                    setError(null);
                  } else {
                    setChosen(act);
                    void run(act);
                  }
                }}
              >
                {EXPENSE_SUPERVISOR_ACT_LABELS[act]}
              </Button>
            ))}
          </ModalActions>
        )}
      </div>
    </Modal>
  );
}
